import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { ShieldAlert, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "/logo192.png";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [totpDigits, setTotpDigits] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (needs2FA && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [needs2FA]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setMfaToken(result.mfa_token);
        setNeeds2FA(true);
        setLoading(false);
        return;
      }
      toast.success("Login successful");
      navigate("/dashboard");
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      toast.error(detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleTotpDigitChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newDigits = [...totpDigits];
    newDigits[index] = value;
    setTotpDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleTotpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !totpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleTotpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setTotpDigits(pasted.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    const code = totpDigits.join("");
    if (code.length !== 6) {
      toast.error("Please enter all 6 digits");
      return;
    }

    setLoading(true);
    try {
      await login(null, null, { mfaToken, code });
      toast.success("Login successful");
      navigate("/dashboard");
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      toast.error(detail || "Invalid verification code");
      setTotpDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setNeeds2FA(false);
    setMfaToken("");
    setTotpDigits(["", "", "", "", "", ""]);
  };

  // ====== 2FA STEP ======
  if (needs2FA) {
    return (
      <div className="min-h-screen flex bg-slate-950">
        <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950/30 to-slate-950" />
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(59,130,246,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(6,182,212,0.06) 0%, transparent 40%)',
          }} />
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }} />
          <div className="relative z-10 flex flex-col justify-center items-center p-16 w-full">
            <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-8" style={{ boxShadow: '0 0 40px rgba(16,185,129,0.1)' }}>
              <ShieldCheck className="h-10 w-10 text-emerald-400" />
            </div>
            <h2 className="text-4xl font-bold text-white text-center mb-4 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Two-Factor
              <span className="block text-emerald-400">Authentication</span>
            </h2>
            <p className="text-slate-400 text-center max-w-sm text-sm leading-relaxed mt-2">
              Enter the 6-digit code from your Google Authenticator app
              to verify your identity.
            </p>
          </div>
        </div>

        <div className="w-full lg:w-[45%] flex items-center justify-center p-6 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-slate-900 lg:from-slate-900/50 lg:to-slate-900" />
          <Card className="relative z-10 w-full max-w-[380px] bg-slate-900/80 backdrop-blur-xl border-slate-800/60 shadow-2xl shadow-black/20">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="relative inline-block mb-4">
                  <div className="absolute -inset-2 bg-gradient-to-br from-emerald-500/15 to-cyan-500/10 rounded-2xl blur-md" />
                  <div className="relative h-14 w-14 rounded-2xl overflow-hidden flex items-center justify-center bg-emerald-500/10 border border-emerald-500/20">
                    <ShieldCheck className="h-7 w-7 text-emerald-400" />
                  </div>
                </div>
                <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Verification Required
                </h1>
                <p className="text-xs text-slate-500 mt-2">
                  Open Google Authenticator and enter the code
                </p>
              </div>

              <form onSubmit={handle2FASubmit} className="space-y-6">
                <div className="flex justify-center gap-2" onPaste={handleTotpPaste}>
                  {totpDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleTotpDigitChange(i, e.target.value)}
                      onKeyDown={(e) => handleTotpKeyDown(i, e)}
                      data-testid={`totp-digit-${i}`}
                      className="w-11 h-13 text-center text-xl font-bold bg-slate-950/50 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 outline-none transition-all"
                    />
                  ))}
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold shadow-lg shadow-emerald-600/20 transition-all active:scale-[0.98]"
                  disabled={loading || totpDigits.join("").length !== 6}
                  data-testid="verify-2fa-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Sign In"
                  )}
                </Button>

                <button
                  type="button"
                  onClick={handleBack}
                  className="w-full text-center text-sm text-slate-500 hover:text-slate-300 transition-colors"
                  data-testid="back-to-login"
                >
                  Back to login
                </button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ====== NORMAL LOGIN ======
  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* Left Panel - Visual */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950/30 to-slate-950" />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(59,130,246,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(6,182,212,0.06) 0%, transparent 40%)',
        }} />
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        
        <div className="relative z-10 flex flex-col justify-center items-center p-16 w-full">
          <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20 mb-8" style={{ boxShadow: '0 0 40px rgba(59,130,246,0.1)' }}>
            <ShieldAlert className="h-10 w-10 text-blue-400" />
          </div>
          <h2 className="text-4xl font-bold text-white text-center mb-4 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            AI-Powered Call
            <span className="block gradient-text">Intelligence Platform</span>
          </h2>
          <p className="text-slate-400 text-center max-w-sm text-sm leading-relaxed mt-2">
            Protect your pay-per-call campaigns with advanced fraud detection, 
            spam analysis, and real-time publisher monitoring.
          </p>
          <div className="flex items-center gap-6 mt-12">
            {[
              { label: "Fraud Detection", value: "AI" },
              { label: "Real-time", value: "24/7" },
              { label: "Accuracy", value: "99%" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl font-bold text-blue-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</div>
                <div className="text-[11px] text-slate-500 mt-1 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-slate-900 lg:from-slate-900/50 lg:to-slate-900" />
        <Card className="relative z-10 w-full max-w-[380px] bg-slate-900/80 backdrop-blur-xl border-slate-800/60 shadow-2xl shadow-black/20">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="relative inline-block mb-4">
                <div className="absolute -inset-2 bg-gradient-to-br from-blue-500/15 to-cyan-500/10 rounded-2xl blur-md" />
                <div className="relative h-14 w-24 rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-xl shadow-black/40 bg-slate-900 flex items-center justify-center p-2">
                  <img src={LOGO_URL} alt="DMM" className="h-full w-full object-contain" style={{ mixBlendMode: 'screen' }} />
                </div>
              </div>
              <h1 className="text-[26px] font-bold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Callintell<span className="text-blue-400">&trade;</span>
              </h1>
              <div className="flex items-center justify-center gap-2 mt-2">
                <div className="h-px w-8 bg-gradient-to-r from-transparent to-slate-700" />
                <p className="text-[10px] text-slate-500 tracking-[0.2em] uppercase font-medium">
                  by Digital Master Media LLC
                </p>
                <div className="h-px w-8 bg-gradient-to-l from-transparent to-slate-700" />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs text-slate-400 font-medium uppercase tracking-wider">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="h-11 bg-slate-950/50 border-slate-800 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 text-slate-200 placeholder:text-slate-600 transition-all"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs text-slate-400 font-medium uppercase tracking-wider">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                    className="h-11 pr-10 bg-slate-950/50 border-slate-800 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 text-slate-200 placeholder:text-slate-600 transition-all"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-11 w-11 text-slate-500 hover:text-slate-300 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold shadow-lg shadow-blue-600/20 transition-all active:scale-[0.98]"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
            <p className="mt-6 text-center text-sm text-slate-500">
              {/* Signup hidden — contact admin for access */}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
